import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useZodForm } from '@/shared/hooks/useZodForm.ts';
import { Button } from '@schuettflix/planum-react';
import { TextFieldInput } from '@/shared/inputs/TextFieldInput.tsx';
import { ReactHookFormDevTool } from '@/shared/components/ReactHookFormDevTool.tsx';
import {
    getClientInformationFormSchema,
    ClientInformationFormValues,
} from '@/shared/forms/ClientInformationForm/schemas/getClientInformationFormSchema.ts';
import { useFormClear } from '@/shared/hooks/useFormClear.ts';
import { SelectOrganizationInput } from '@/shared/inputs/SelectOrganizationInput.tsx';
import { SelectConstructionProjectInput } from '@/shared/inputs/SelectConstructionProjectInput.tsx';
import { SelectUserInput } from '@/shared/inputs/SelectUserInput.tsx';

const SFLX_DE_ORG_ID = 258;

type ClientInformationFormProps = {
    defaultValues?: Partial<ClientInformationFormValues>;
    defaultValueLabels?: {
        organization?: string;
        constructionProject?: string;
        user?: string;
        sourceCostCenter?: string;
        manualCostCenter?: string;
    };
    formRules?: {
        costCenter?: {
            disabled?: boolean;
            required?: boolean;
        };
        organization?: {
            disabled?: boolean;
        };
        constructionProject?: {
            disabled?: boolean;
        };
        user?: {
            disabled?: boolean;
        };
    };
    showSubmitButton?: boolean;
    mode?: 'create' | 'edit';
    onChange?: (values: Partial<ClientInformationFormValues>) => void;
    onSubmit?: (values: ClientInformationFormValues) => void;
};

export const ClientInformationForm = ({
    defaultValues,
    defaultValueLabels,
    formRules,
    showSubmitButton = true,
    mode = 'create',
    onChange,
    onSubmit,
}: ClientInformationFormProps) => {
    const { t } = useTranslation();
    const [isCostCenterRequired, setIsCostCenterRequired] = useState(formRules?.costCenter?.required ?? false);

    const methods = useZodForm({
        schema: getClientInformationFormSchema({
            errorMessages: {
                required: t('product.addPosition.errorMessage.pleaseEnterValue'),
                costCenter: {
                    required: t('sections.clientInformation.costCenter.requiredError'),
                },
            },
            formRules: { costCenter: { required: isCostCenterRequired } },
        }),
        defaultValues: {
            organizationId: defaultValues?.organizationId,
            userId: defaultValues?.userId,
            constructionProjectId: defaultValues?.constructionProjectId,
            projectNumber: defaultValues?.projectNumber,
            manualCostCenter: defaultValues?.manualCostCenter ?? null,
            sourceCostCenter: defaultValues?.sourceCostCenter ?? null,
        },
    });

    const { control, handleSubmit, watch, clearField, register, setValue, clearErrors } = useFormClear(methods);

    const formValues = watch();

    useEffect(() => {
        const { unsubscribe } = watch(values => {
            onChange?.({
                organizationId: values.organizationId,
                constructionProjectId: values.constructionProjectId,
                userId: values.userId,
                manualCostCenter: values.manualCostCenter || null,
                sourceCostCenter: values.sourceCostCenter || null,
            });
        });
        return unsubscribe;
    }, [watch, onChange]);

    const handleFormSubmit = useCallback<React.FormEventHandler<HTMLFormElement>>(
        async event => {
            event.preventDefault();
            await handleSubmit(values => {
                onSubmit?.(values);
            }, console.error)();
        },
        [handleSubmit, onSubmit]
    );

    return (
        <FormProvider {...methods}>
            <form className="flex w-full flex-col gap-8" data-test="customer-details-form" onSubmit={handleFormSubmit}>
                <div className="grid gap-4">
                    <SelectOrganizationInput
                        {...register('organizationId')}
                        defaultValue={defaultValues?.organizationId}
                        defaultValueLabel={defaultValueLabels?.organization}
                        label={t('sections.clientInformation.selectOrganization.label')}
                        dataTest="organization-input"
                        filters={{ organizationType: 'client' }}
                        exclude={{ types: ['platform'], ids: [SFLX_DE_ORG_ID] }}
                        disabled={formRules?.organization?.disabled}
                        onValueChange={org => {
                            setIsCostCenterRequired(org?.costcenterMandatory ?? false);
                            clearField('constructionProjectId');
                            clearField('projectNumber');
                            clearField('userId');
                            clearField('sourceCostCenter');
                            clearErrors();
                        }}
                    />

                    <SelectConstructionProjectInput
                        {...register('constructionProjectId')}
                        defaultValue={defaultValues?.constructionProjectId}
                        defaultValueLabel={defaultValueLabels?.constructionProject}
                        organizationId={formValues.organizationId}
                        filters={{ statuses: ['ACTIVE', 'INACTIVE'] }}
                        disabled={!formValues.organizationId || formRules?.constructionProject?.disabled}
                        dataTest="construction-project-input"
                        onValueChange={project => {
                            if (project) {
                                setValue('projectNumber', project.projectNumber);
                                setValue('sourceCostCenter', project.costCenter ?? null);
                            } else {
                                clearField('projectNumber');
                                clearField('sourceCostCenter');
                            }
                        }}
                    />

                    <SelectUserInput
                        {...register('userId')}
                        defaultValue={defaultValues?.userId}
                        defaultValueLabel={defaultValueLabels?.user}
                        label={t('sections.clientInformation.selectUser.label')}
                        filters={{ isActive: true, organization: formValues.organizationId }}
                        disabled={!formValues.organizationId || formRules?.user?.disabled}
                        dataTest="user-input"
                    />

                    <TextFieldInput
                        {...register('sourceCostCenter')}
                        label={defaultValueLabels?.sourceCostCenter}
                        disabled
                        size="lg"
                    />

                    <TextFieldInput
                        {...register('manualCostCenter')}
                        label={defaultValueLabels?.manualCostCenter}
                        placeHolder={t('sections.clientInformation.costCenter.label')}
                        disabled={!formValues.organizationId || formRules?.costCenter?.disabled}
                        size="lg"
                    />
                </div>

                {showSubmitButton && (
                    <footer className="flex justify-center">
                        <Button data-test="proceed" variant={mode === 'create' ? 'primary' : 'secondary'}>
                            {t('sections.clientInformation.nextButton')}
                        </Button>
                    </footer>
                )}

                <ReactHookFormDevTool control={control} />
            </form>
        </FormProvider>
    );
};
